/**
 * Shop Page
 * Fetches native products from our database
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useBackgroundRemoval } from '../hooks/useBackgroundRemoval';
import {
  ShoppingCartIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MarketplaceBanner from '../components/events/MarketplaceBanner';
import { initAffiliateTracking, getAffiliateRef } from '../utils/affiliate-tracking';
import { getStrikeCheckoutInvoice, pollStrikeInvoiceStatus, getStripeCheckoutUrl, getStripeCheckoutUrlByPriceId, getProdigiCheckoutUrl, getProdigiStrikeInvoice, ProdigiShippingRecipient } from '../utils/checkout-utils';
import { LightningPaymentModal } from "../components/shop/LightningPaymentModal";
import { PrintMockupPreview, MockupBounds } from '../components/shop/PrintMockupPreview';
import { ShippingAddressForm, EMPTY_SHIPPING_FORM, isShippingFormComplete } from '../components/shop/ShippingAddressForm';
import { TEST_PRODUCTS } from '../data/test-products';
import { STRIPE_PRODUCTS } from '../data/stripe-products';
import { transformProduct } from '../../lib/fourthwall/utils';
import { Helmet } from 'react-helmet-async';

const FOURTHWALL_BASE = 'https://storefront-api.fourthwall.com';

interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  compareAtPrice?: number;
  currency: string;
  images: string[];
  handle: string;
  available: boolean;
  url: string;
  category?: string;
  featured?: boolean;
  stripePriceId?: string;
  productKind?: 'physical' | 'digital';
  fulfillment?: 'prodigi';
  prodigiProductId?: string;
  prodigiSku?: string;
  mockupTemplateUrl?: string | null;
  mockupBounds?: MockupBounds | null;
}

export default function Shop({ hideBanner = false, embedded = false }: { hideBanner?: boolean; embedded?: boolean }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  // Background removal tracking across all product cards
  const [settledCount, setSettledCount] = useState(0);
  const [newlyEnhanced, setNewlyEnhanced] = useState(0);
  // reloadCountdown removed — auto-reload caused images to revert when Supabase upload failed

  const handleImageSettled = useCallback((wasNew: boolean) => {
    setSettledCount(s => s + 1);
    if (wasNew) setNewlyEnhanced(n => n + 1);
  }, []);

  // Reset tracking counters whenever the product list changes
  useEffect(() => {
    setSettledCount(0);
    setNewlyEnhanced(0);
  }, [products.length]);

  // Broadcast progress so the gallery-level banner stays visible even on the Gallery tab
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('bgr:progress', {
      detail: { remaining: products.length - settledCount, total: products.length, newlyEnhanced }
    }));
  }, [settledCount, products.length, newlyEnhanced]);

  const [lightningPayment, setLightningPayment] = useState<{
    invoiceId: string;
    lnInvoice: string;
    expirationInSec: number;
    amount: number;
    description: string;
  } | null>(null);

  const FOURTHWALL_BASE = 'https://storefront-api.fourthwall.com';

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const hasRef = searchParams.get('ref') || searchParams.get('affiliate') || searchParams.get('aff');

  // If there's an affiliate ref on any page other than /, redirect home so the ref
  // is captured by the root layout's affiliate tracking (avoids an infinite loop at /).
  if (hasRef && location.pathname !== '/') {
    return <Navigate to={`/${location.search}`} replace />;
  }

  // Initialize affiliate tracking on page load
  useEffect(() => {
    initAffiliateTracking();
  }, []);

  // Restore scroll position + the clicked product after the user returns
  // (browser back) from an external product page. ProductCard writes
  // 'shopReturn' to sessionStorage right before navigating to Fourthwall.
  useEffect(() => {
    if (loading || products.length === 0) return;
    let raw: string | null = null;
    try { raw = sessionStorage.getItem('shopReturn'); } catch { return; }
    if (!raw) return;
    try { sessionStorage.removeItem('shopReturn'); } catch { /* ignore */ }
    let saved: { y?: number; id?: string };
    try { saved = JSON.parse(raw); } catch { return; }
    const t = setTimeout(() => {
      // Center the product in its (mobile) carousel, then restore page scroll.
      if (saved.id) {
        const el = document.querySelector(`[data-product-id="${CSS.escape(String(saved.id))}"]`);
        el?.scrollIntoView({ block: 'nearest', inline: 'center' });
      }
      if (typeof saved.y === 'number') window.scrollTo(0, saved.y);
    }, 150);
    return () => clearTimeout(t);
  }, [loading, products]);

  // Fetch native products from our API
  useEffect(() => {
    // In dev, bypass API and load local test products so the page
    // works even when API routes aren't running (e.g., `vite` without `vercel dev`).
    if (import.meta.env.DEV) {
      setProducts([...STRIPE_PRODUCTS, ...TEST_PRODUCTS]);
      setLoading(false);
      return;
    }

    async function fetchProducts() {
      try {
        setLoading(true);
        setError(null);

        // Try server API endpoints first (primary path + explicit Fourthwall path)
        const apiProducts = await fetchProductsFromApi();
        if (apiProducts.length > 0) {
          setProducts([...STRIPE_PRODUCTS, ...apiProducts]);
          return;
        }

        // If API returns empty, try direct Fourthwall with public token (prod-only path)
        // Use environment variable or fallback to known public token
        // Use environment variable or fallback to known public token (split to avoid aggressive secret scanning)
        const part1 = 'ptkn_cbf6cf10-8047-4150';
        const part2 = '-b47c-3fd2befcaa34';
        const fallbackToken = import.meta.env.VITE_FOURTHWALL_STOREFRONT_TOKEN || (part1 + part2);
        if (fallbackToken) {
          const fwProducts = await fetchFourthwallDirect(fallbackToken);
          if (fwProducts.length > 0) {
            setProducts([...STRIPE_PRODUCTS, ...fwProducts]);
            return;
          }
        }

        // Even if external sources fail, surface the native Stripe products.
        if (STRIPE_PRODUCTS.length > 0) {
          setProducts([...STRIPE_PRODUCTS]);
          return;
        }

        setError('Failed to load products. Please try again.');
        setProducts([]);
      } catch (err) {
        console.error('Error fetching products via API, trying Fourthwall direct fallback:', err);
        setError('Failed to load products. Please try again.');
        setProducts([]);
      } finally {
        setLoading(false);
      }
    }

    fetchProducts();
  }, []);

  // Extract unique categories from products
  const categories = ['all', ...Array.from(new Set(products.map(p => p.category || 'general').filter(Boolean)))];

  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === 'all' || (product.category || 'general') === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredProducts = filteredProducts.filter((p) => p.featured);
  const regularProducts = filteredProducts.filter((p) => !p.featured);

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center py-12">
          <p className="text-red-400 text-lg mb-2">Error loading products</p>
          <p className="text-white/60">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Page-level SEO belongs to the page at /shop, not to the shop tab that
          Gallery mounts inside the homepage and the /services pages. Helmet
          resolves duplicate tags last-mount-wins, so an embedded Shop was
          overwriting those URLs with the shop's title and — worse — telling
          crawlers the homepage canonicalises to /shop. */}
      {!embedded && (
        <Helmet>
          <title>THE LOST+UNFOUNDS | Shop</title>
          <meta name="description" content="Shop exclusive THE LOST+UNFOUNDS apparel, gear, and digital products. Discover unique findings from the field, delivered directly to your door." />
          <link rel="canonical" href="https://www.thelostandunfounds.com/shop" />
        </Helmet>
      )}

      {!hideBanner && <MarketplaceBanner surface="shop" />}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 space-y-8">
        {!embedded && (
          <>
            {/* Header */}
            <div className="text-center space-y-3 sm:space-y-4">
              <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-white">SHOP</h1>
            </div>

            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 sm:py-2.5 bg-black/60 border border-white rounded-none text-white placeholder-white/50 text-base
                           hover:border-white hover:bg-black/70
                           focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white focus:bg-black/80
                           transition-all duration-150 shadow-lg hover:shadow-white/10 focus:shadow-white/20
                           min-h-[48px] touch-action: manipulation"
                />
              </div>
              <div className="flex items-center sm:w-56 relative">
                <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 flex-shrink-0 pointer-events-none" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 sm:py-2.5 bg-black/60 border border-white rounded-none text-white text-base
                           hover:border-white hover:bg-black/70
                           focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white focus:bg-black/80
                           transition-all duration-150 shadow-lg hover:shadow-white/10 focus:shadow-white/20 cursor-pointer
                           min-h-[48px] touch-action: manipulation appearance-none"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}

        {/* Skeleton — shown while products are loading */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 animate-pulse">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i}>
                <div className="aspect-square bg-white/5" />
                <div className="p-4 space-y-2">
                  <div className="h-3 bg-white/5 w-3/4" />
                  <div className="h-3 bg-white/5 w-1/4" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Featured Products */}
        {!loading && featuredProducts.length > 0 && (
          <div className="space-y-4 sm:space-y-6">
            <h2 className="text-xl sm:text-2xl font-bold text-white">Featured Products</h2>
            <ProductRow products={featuredProducts} onOpen={setSelectedProduct} onSettled={handleImageSettled} />
          </div>
        )}

        {/* Regular Products */}
        {!loading && regularProducts.length > 0 && (
          <div className="space-y-4 sm:space-y-6">
            <ProductRow products={regularProducts} onOpen={setSelectedProduct} onSettled={handleImageSettled} />
          </div>
        )}

        {/* Empty State */}
        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-white/70 text-lg">No products found</p>
            <p className="text-white/50 mt-2">
              Try adjusting your search or filter criteria
            </p>
          </div>
        )}

        {selectedProduct && (
          <ProductModal
            product={selectedProduct}
            onClose={() => setSelectedProduct(null)}
            affiliateRef={getAffiliateRef()}
            onStrikeCheckout={setLightningPayment}
          />
        )}

        {lightningPayment && (
          <LightningPaymentModal
            invoiceId={lightningPayment.invoiceId}
            lnInvoice={lightningPayment.lnInvoice}
            expirationInSec={lightningPayment.expirationInSec}
            amount={lightningPayment.amount}
            description={lightningPayment.description}
            onClose={() => setLightningPayment(null)}
          />
        )}
      </div>
    </>
  );
}

async function fetchProductsFromApi(): Promise<Product[]> {
  const endpoints = ['/api/shop/products', '/api/shop/fourthwall/products'];

  for (const endpoint of endpoints) {
    try {
      const response = await fetch(endpoint, { cache: 'no-store' });
      const contentType = response.headers.get('content-type') || '';
      const bodyText = await response.text();

      if (!response.ok) {
        console.warn(`API ${endpoint} returned ${response.status}: ${bodyText.slice(0, 200)}`);
        continue;
      }

      if (!contentType.includes('application/json')) {
        console.warn(
          `API ${endpoint} unexpected content type: ${contentType}. Body: ${bodyText.slice(
            0,
            200
          )}`
        );
        continue;
      }

      let data: any = null;
      try {
        data = JSON.parse(bodyText);
      } catch (parseErr) {
        console.warn(`API ${endpoint} JSON parse failed:`, parseErr);
        continue;
      }

      if (data.error) {
        console.warn(`API ${endpoint} error payload: ${data.message || data.error}`);
        continue;
      }

      const products = data.products || [];
      if (products.length > 0) {
        return products;
      }
    } catch (error) {
      console.warn(`API ${endpoint} failed:`, error);
    }
  }

  return [];
}

async function fetchFourthwallDirect(token: string): Promise<Product[]> {
  const endpoints = [
    `${FOURTHWALL_BASE}/v1/collections/all/products?storefront_token=${token}`,
    `${FOURTHWALL_BASE}/v1/products?storefront_token=${token}`,
    `${FOURTHWALL_BASE}/v1/shop/feed?storefront_token=${token}`,
  ];

  for (const endpoint of endpoints) {
    try {
      const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
      const contentType = response.headers.get('content-type') || '';
      const body = await response.text();

      if (!contentType.includes('application/json')) {
        console.warn(`Fourthwall fallback unexpected content type ${contentType} for ${endpoint}`);
        continue;
      }

      if (!response.ok) {
        console.warn(`Fourthwall fallback ${endpoint} returned ${response.status}: ${body.slice(0, 200)}`);
        continue;
      }

      const data = JSON.parse(body);
      const offers = extractOffers(data);
      if (offers.length === 0) {
        continue;
      }

      return offers.map((offer) => {
        const transformed = transformProduct(offer);
        return {
          id: transformed.id,
          title: transformed.title,
          description: transformed.description,
          price: transformed.price,
          compareAtPrice: transformed.compareAtPrice,
          currency: transformed.currency,
          images: transformed.images,
          handle: transformed.handle,
          available: transformed.available,
          url: transformed.url,
        };
      });
    } catch (error) {
      console.warn(`Fourthwall fallback failed for ${endpoint}:`, error);
    }
  }

  return [];
}

function extractOffers(data: any): any[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.offers)) return data.offers;
  if (Array.isArray(data.products)) return data.products;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.results)) return data.results;
  return [];
}

function ProductCard({ product, onOpen, onSettled }: { product: Product; onOpen: () => void; onSettled?: (wasNew: boolean) => void }) {
  const { user } = useAuth();
  const imageUrl = product.images && product.images.length > 0 ? product.images[0] : null;
  const displayPrice = product.price;
  const displayComparePrice = product.compareAtPrice || null;
  const { processedUrl, processing, error: bgrError } = useBackgroundRemoval(imageUrl, onSettled);

  // If the processed (Supabase-hosted) image fails to load — e.g. storage is
  // down or over its egress quota — fall back to the original product photo
  // WITHOUT the styled outline. Reset the flag whenever the processed URL changes.
  const [processedFailed, setProcessedFailed] = useState(false);
  useEffect(() => { setProcessedFailed(false); }, [processedUrl]);
  const showProcessed = !!processedUrl && !processedFailed;
  const displayUrl = showProcessed ? processedUrl : imageUrl;

  // Get affiliate ref for tracking
  const affiliateRef = getAffiliateRef();

  // Clicking a card goes STRAIGHT to the Fourthwall product page (no lightbox).
  // Non-Fourthwall products (e.g. Prodigi prints) still open the modal, which
  // handles their shipping/checkout flow.
  const isFourthwall = !!product.url && product.url.includes('fourthwall.com');
  const handleCardClick = () => {
    if (isFourthwall && product.url) {
      if (affiliateRef) {
        import('../utils/affiliate-tracking').then(({ trackAffiliateClick }) => {
          trackAffiliateClick(affiliateRef);
        });
      }
      // Remember where we were so we can restore scroll + this product when the
      // user hits back from Fourthwall (see restore effect in Shop).
      try {
        sessionStorage.setItem('shopReturn', JSON.stringify({ y: window.scrollY, id: product.id }));
      } catch { /* private mode / quota — restore just won't fire */ }
      window.location.href = product.url;
      return;
    }
    onOpen();
  };

  return (
    <div
      className="transition-all duration-300 h-full cursor-pointer flex flex-col"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      {/* Product image */}
      <div className="relative aspect-square bg-black overflow-hidden">
        {displayUrl ? (
          <img
            src={displayUrl}
            alt={product.title}
            className="w-full h-full transition-all duration-700"
            onError={() => {
              // Only revert once — if the ORIGINAL image also fails there's
              // nothing better to show, so don't loop.
              if (showProcessed) setProcessedFailed(true);
            }}
            style={showProcessed ? {
              // The white sticker outline is baked into the transparent PNG by
              // scripts/generate-transparent-product-images.mjs — no CSS filter.
              objectFit: 'contain',
              padding: '24px',
            } : {
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />
        ) : (
          <div className="w-full h-full bg-white/5" />
        )}

        {/* Background removal error — temporary debug indicator */}
        {bgrError && !processing && (
          <div className="absolute bottom-2 left-2 right-2 pointer-events-none">
            <p className="text-[8px] text-red-400 bg-black/80 px-2 py-1 leading-tight break-all">{bgrError}</p>
          </div>
        )}

        {/* Processing indicator — visible while AI removes background */}
        {processing && (
          <div className="absolute inset-0 flex flex-col items-center justify-end pb-4 pointer-events-none">
            <div className="bg-black/70 backdrop-blur-sm px-3 py-1.5 flex items-center gap-2">
              <span className="flex gap-[3px]">
                <span className="w-1 h-1 bg-white rounded-full animate-[loading-dot_1.2s_infinite_0s]" />
                <span className="w-1 h-1 bg-white rounded-full animate-[loading-dot_1.2s_infinite_0.2s]" />
                <span className="w-1 h-1 bg-white rounded-full animate-[loading-dot_1.2s_infinite_0.4s]" />
              </span>
              <span className="text-[9px] font-black uppercase tracking-widest text-white/60">Enhancing</span>
            </div>
          </div>
        )}
      </div>

      {/* Text below the image — centered title + price, no description */}
      <div className="p-4 flex-1 flex flex-col gap-1 items-center text-center">
        <h3 className="text-sm font-black text-white uppercase tracking-tight leading-tight line-clamp-2">
          {product.title.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim()}
        </h3>
        <div className="flex items-center justify-center gap-2 text-white mt-1">
          {displayPrice === 0 ? (
            <span className="text-sm font-black">Free</span>
          ) : (
            <>
              <span className="text-sm font-black">${displayPrice.toFixed(2)}</span>
              {displayComparePrice && displayComparePrice > displayPrice && (
                <span className="text-xs text-white/40 line-through">
                  ${displayComparePrice.toFixed(2)}
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * A row of products. On mobile: a horizontal snap-scroll carousel showing ONE
 * product at a time, with a dot indicator + "swipe" hint. On desktop (md+): a
 * 3-column grid.
 */
function ProductRow({
  products,
  onOpen,
  onSettled,
}: {
  products: Product[];
  onOpen: (p: Product) => void;
  onSettled?: (wasNew: boolean) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || el.children.length < 2) return;
    const stride =
      (el.children[1] as HTMLElement).offsetLeft - (el.children[0] as HTMLElement).offsetLeft;
    if (stride > 0) {
      setActive(Math.max(0, Math.min(products.length - 1, Math.round(el.scrollLeft / stride))));
    }
  };

  return (
    <div className="space-y-4">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex md:grid md:grid-cols-3 gap-4 sm:gap-6 overflow-x-auto md:overflow-visible snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {products.map((product) => (
          <div key={product.id} data-product-id={product.id} className="snap-center shrink-0 w-full sm:w-[46%] md:w-auto">
            <ProductCard product={product} onOpen={() => onOpen(product)} onSettled={onSettled} />
          </div>
        ))}
      </div>

      {/* Swipe indicator — mobile only */}
      {products.length > 1 && (
        <div className="flex md:hidden flex-col items-center gap-2 pt-1">
          <div className="flex items-center gap-2">
            {products.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === active ? 'w-6 bg-white' : 'w-1.5 bg-white/25'
                }`}
              />
            ))}
          </div>
          {active === 0 && (
            <span className="text-[9px] font-black uppercase tracking-[0.25em] text-white/30 animate-pulse">
              Swipe →
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function ProductModal({
  product,
  onClose,
  affiliateRef,
  onStrikeCheckout,
}: {
  product: Product;
  onClose: () => void;
  affiliateRef: string | null;
  onStrikeCheckout: (payment: { invoiceId: string; lnInvoice: string; expirationInSec: number; amount: number; description: string }) => void;
}) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const { user } = useAuth();
  const imageUrl = product.images && product.images.length > 0 ? product.images[0] : null;
  const displayPrice = product.price;
  const displayComparePrice = product.compareAtPrice || null;
  const isFourthwallProduct = product.url?.includes('fourthwall.com');
  const isProdigiProduct = product.fulfillment === 'prodigi';

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [showShippingForm, setShowShippingForm] = useState(false);
  const [shipping, setShipping] = useState(EMPTY_SHIPPING_FORM);
  const [shippingError, setShippingError] = useState<string | null>(null);

  const handleCheckoutClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (isFourthwallProduct && product.url) {
      window.location.href = product.url;
      return;
    }
    if (isProdigiProduct) {
      // Strike has no hosted address-collection step, so physical Prodigi
      // prints need the shipping address up front before we create the
      // Lightning invoice.
      setShowShippingForm(true);
      return;
    }
    if (affiliateRef) {
      import('../utils/affiliate-tracking').then(({ trackAffiliateClick }) => {
        trackAffiliateClick(affiliateRef);
      });
    }
    try {
      setCheckoutLoading(true);
      console.log('⚡ Starting Strike (Bitcoin) checkout for product:', {
        id: product.id,
        title: product.title,
        price: product.price,
      });

      const result = await getStrikeCheckoutInvoice({
        amount: product.price,
        currency: product.currency || 'USD',
        description: product.title,
        productId: product.id,
        affiliateRef,
      });

      console.log('⚡ Strike invoice created:', result.invoiceId);

      // Close product modal and open Lightning payment modal
      onClose();
      onStrikeCheckout({
        invoiceId: result.invoiceId,
        lnInvoice: result.lnInvoice,
        expirationInSec: result.expirationInSec,
        amount: product.price,
        description: product.title,
      });
    } catch (error: any) {
      console.error('❌ Error creating Strike checkout:', error);
      alert(`Checkout Error: ${error.message || 'Failed to start checkout. Please try again.'}`);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleProdigiShippingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShippingError(null);

    if (!isShippingFormComplete(shipping)) {
      setShippingError('Please fill in all required fields.');
      return;
    }
    if (!product.prodigiProductId) {
      setShippingError('This product is missing its Prodigi catalog reference.');
      return;
    }

    if (affiliateRef) {
      import('../utils/affiliate-tracking').then(({ trackAffiliateClick }) => {
        trackAffiliateClick(affiliateRef);
      });
    }

    try {
      setCheckoutLoading(true);
      const recipient: ProdigiShippingRecipient = {
        name: shipping.name,
        email: shipping.email,
        address: {
          line1: shipping.line1,
          line2: shipping.line2 || undefined,
          townOrCity: shipping.townOrCity,
          stateOrCounty: shipping.stateOrCounty || undefined,
          postalOrZipCode: shipping.postalOrZipCode,
          countryCode: shipping.countryCode,
        },
      };

      const result = await getProdigiStrikeInvoice({
        productId: product.prodigiProductId,
        recipient,
        affiliateRef,
      });

      onClose();
      onStrikeCheckout({
        invoiceId: result.invoiceId,
        lnInvoice: result.lnInvoice,
        expirationInSec: result.expirationInSec,
        amount: product.price,
        description: product.title,
      });
    } catch (error: any) {
      console.error('❌ Error creating Prodigi Strike checkout:', error);
      setShippingError(error.message || 'Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleStripeCheckoutClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (isFourthwallProduct && product.url) {
      window.location.href = product.url;
      return;
    }
    if (affiliateRef) {
      import('../utils/affiliate-tracking').then(({ trackAffiliateClick }) => {
        trackAffiliateClick(affiliateRef);
      });
    }
    try {
      setStripeLoading(true);
      console.log('💳 Starting Stripe (card) checkout for product:', {
        id: product.id,
        title: product.title,
        price: product.price,
        stripePriceId: product.stripePriceId,
      });

      // Prodigi prints go through their own checkout route (pre-creates the
      // linked prodigi_orders row); Stripe collects shipping on its hosted
      // page. Price-ID products (native Stripe catalog) use
      // /api/checkout/create-session. Everything else falls back to the
      // inline price_data flow on /api/shop/payments/stripe.
      const result = isProdigiProduct && product.prodigiProductId
        ? await getProdigiCheckoutUrl({
            productId: product.prodigiProductId,
            affiliateRef,
          })
        : product.stripePriceId
        ? await getStripeCheckoutUrlByPriceId({
            priceId: product.stripePriceId,
            productKind: product.productKind || 'physical',
            productId: product.id,
            affiliateRef,
          })
        : await getStripeCheckoutUrl({
            amount: product.price,
            currency: product.currency || 'USD',
            description: product.title,
            productId: product.id,
            affiliateRef,
          });

      console.log('💳 Stripe session created, redirecting:', result.sessionId);

      // Hand off to Stripe's hosted checkout page.
      window.location.href = result.url;
    } catch (error: any) {
      console.error('❌ Error creating Stripe checkout:', error);
      alert(`Checkout Error: ${error.message || 'Failed to start checkout. Please try again.'}`);
      setStripeLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="relative bg-black/90 border border-white rounded-none shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          type="button"
          className="absolute top-3 right-3 text-white/80 hover:text-white flex items-center justify-center w-10 h-10 z-10"
          aria-label="Close"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>

        <div className="grid md:grid-cols-2 gap-0 md:h-auto">
          {imageUrl && (
            <div className="relative bg-white/5 max-h-[40vh] md:max-h-full aspect-square md:aspect-auto flex items-center justify-center overflow-hidden">
              {isProdigiProduct ? (
                <PrintMockupPreview
                  artworkUrl={imageUrl}
                  templateUrl={product.mockupTemplateUrl}
                  bounds={product.mockupBounds}
                  alt={product.title}
                  className="relative w-full h-full"
                />
              ) : (
                <img
                  src={imageUrl}
                  alt={product.title}
                  className="w-full h-full object-contain"
                />
              )}
            </div>
          )}
          <div className="p-5 sm:p-6 space-y-3 md:space-y-4 overflow-hidden">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {product.featured && (
                  <span className="bg-yellow-400/20 text-yellow-300 text-[11px] font-semibold px-2 py-1 rounded-none uppercase tracking-[0.08em]">
                    Featured
                  </span>
                )}
                {product.category && (
                  <span className="bg-white/10 text-white text-[11px] px-2 py-1 rounded-none uppercase tracking-[0.08em]">
                    {product.category}
                  </span>
                )}
              </div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-white">
                {product.title}
              </h2>
              <div className="flex items-center gap-3 text-white">
                {displayPrice === 0 ? (
                  <span className="text-2xl font-bold text-green-400">Free</span>
                ) : (
                  <>
                    <span className="text-2xl font-bold">${displayPrice.toFixed(2)}</span>
                    {displayComparePrice && displayComparePrice > displayPrice && (
                      <span className="text-sm text-white/50 line-through">
                        ${displayComparePrice.toFixed(2)}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            <p className="text-white/80 leading-relaxed line-clamp-3">
              {product.description}
            </p>

            {showShippingForm ? (
              <form onSubmit={handleProdigiShippingSubmit} className="space-y-2">
                <ShippingAddressForm value={shipping} onChange={setShipping} />
                {shippingError && <p className="text-xs text-red-400">{shippingError}</p>}
                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={() => setShowShippingForm(false)} className="flex-1 sm:flex-none px-4 py-3 sm:py-2 text-white/60 border border-white/30 rounded-none hover:text-white hover:border-white transition-colors font-semibold text-sm">
                    Back
                  </button>
                  <button type="submit" disabled={checkoutLoading} className={`flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white text-black px-4 py-3 sm:py-2 rounded-none hover:bg-white/90 transition-colors font-semibold text-sm ${checkoutLoading ? 'opacity-50 pointer-events-none' : ''}`}>
                    {checkoutLoading ? <span className="animate-pulse">Creating invoice...</span> : <>Pay with Bitcoin ⚡</>}
                  </button>
                </div>
              </form>
            ) : (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                <a
                  href="#"
                  onClick={handleCheckoutClick}
                  className={`flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white text-black px-4 py-3 sm:py-2 rounded-none hover:bg-white/90 transition-colors font-semibold text-sm sm:text-base min-h-[44px] touch-action: manipulation cursor-pointer ${checkoutLoading ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  {checkoutLoading ? (
                    <span className="animate-pulse">Creating invoice...</span>
                  ) : (
                    <>
                      <ShoppingCartIcon className="w-4 h-4" />
                      {isFourthwallProduct ? 'View on Fourthwall' : displayPrice === 0 ? 'View' : 'Pay with Bitcoin ⚡'}
                    </>
                  )}
                </a>
                {!isFourthwallProduct && displayPrice > 0 && (
                  <a
                    href="#"
                    onClick={handleStripeCheckoutClick}
                    className={`flex-1 sm:flex-none flex items-center justify-center gap-2 bg-transparent text-white border border-white px-4 py-3 sm:py-2 rounded-none hover:bg-white hover:text-black transition-colors font-semibold text-sm sm:text-base min-h-[44px] touch-action: manipulation cursor-pointer ${stripeLoading ? 'opacity-50 pointer-events-none' : ''}`}
                    aria-label="Pay with card"
                  >
                    {stripeLoading ? (
                      <span className="animate-pulse">Redirecting…</span>
                    ) : (
                      <>
                        <CreditCardIcon className="w-4 h-4" />
                        Pay with Card
                      </>
                    )}
                  </a>
                )}
                {!product.available && (
                  <div className="text-xs text-red-400">Out of Stock</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

